import React, { useCallback, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { addDays, differenceInCalendarDays, format, parseISO } from 'date-fns';

const HANDLE_WIDTH = 8;
const DRAG_CLICK_THRESHOLD = 4;

function parseYmd(ymd) {
  if (!ymd) return null;
  try {
    return parseISO(String(ymd).split('T')[0]);
  } catch {
    return null;
  }
}

function toYmd(date) {
  return format(date, 'yyyy-MM-dd');
}

export default function GanttScheduleBar({
  workOrderId,
  scheduleStartYmd,
  scheduleEndYmd,
  windowStart,
  windowEnd,
  dayWidth,
  color,
  usingDefaults = false,
  headline,
  fullTitle,
  showBarLabel,
  onEventClick,
  onScheduleChange,
  onInteractionBusyChange,
  disabled = false,
}) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const dragRef = useRef(null);
  const draftRef = useRef(null);
  const movedRef = useRef(false);

  const startYmd = draft?.start ?? scheduleStartYmd;
  const endYmd = draft?.end ?? scheduleEndYmd;
  const start = parseYmd(startYmd);
  const end = parseYmd(endYmd);

  const applyDraft = useCallback((next) => {
    draftRef.current = next;
    setDraft(next);
  }, []);

  const finishDrag = useCallback(async () => {
    const drag = dragRef.current;
    const pending = draftRef.current;
    dragRef.current = null;
    draftRef.current = null;

    const releaseBusy = () => {
      onInteractionBusyChange?.(false);
    };

    if (!pending || !movedRef.current) {
      setDraft(null);
      releaseBusy();
      if (!movedRef.current && drag?.mode === 'move') {
        onEventClick?.();
      }
      return;
    }

    if (pending.start === scheduleStartYmd && pending.end === scheduleEndYmd) {
      setDraft(null);
      releaseBusy();
      return;
    }

    setSaving(true);
    try {
      await onScheduleChange?.(workOrderId, {
        schedule_start: pending.start,
        schedule_end: pending.end,
      });
      setDraft(null);
    } catch {
      setDraft(null);
    } finally {
      setSaving(false);
      releaseBusy();
    }
  }, [
    onEventClick,
    onInteractionBusyChange,
    onScheduleChange,
    scheduleEndYmd,
    scheduleStartYmd,
    workOrderId,
  ]);

  const beginDrag = useCallback(
    (mode, event) => {
      if (disabled || saving) return;
      event.preventDefault();
      event.stopPropagation();
      onInteractionBusyChange?.(true);

      dragRef.current = {
        mode,
        originStart: scheduleStartYmd,
        originEnd: scheduleEndYmd,
        pointerX: event.clientX,
      };
      movedRef.current = false;
      draftRef.current = null;
      setDraft(null);

      const onPointerMove = (moveEvent) => {
        const drag = dragRef.current;
        if (!drag) return;

        if (Math.abs(moveEvent.clientX - drag.pointerX) > DRAG_CLICK_THRESHOLD) {
          movedRef.current = true;
        }

        const deltaDays = Math.round((moveEvent.clientX - drag.pointerX) / dayWidth);
        const originStart = parseYmd(drag.originStart);
        const originEnd = parseYmd(drag.originEnd);
        if (!originStart || !originEnd) return;

        if (drag.mode === 'move') {
          applyDraft({
            start: toYmd(addDays(originStart, deltaDays)),
            end: toYmd(addDays(originEnd, deltaDays)),
          });
          return;
        }

        if (drag.mode === 'resize-start') {
          const nextStart = addDays(originStart, deltaDays);
          const clampedStart = nextStart > originEnd ? originEnd : nextStart;
          applyDraft({
            start: toYmd(clampedStart),
            end: drag.originEnd,
          });
          return;
        }

        const nextEnd = addDays(originEnd, deltaDays);
        const clampedEnd = nextEnd < originStart ? originStart : nextEnd;
        applyDraft({
          start: drag.originStart,
          end: toYmd(clampedEnd),
        });
      };

      const onPointerUp = () => {
        document.removeEventListener('pointermove', onPointerMove);
        document.removeEventListener('pointerup', onPointerUp);
        finishDrag();
      };

      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
    },
    [applyDraft, dayWidth, disabled, finishDrag, onInteractionBusyChange, saving, scheduleEndYmd, scheduleStartYmd]
  );

  if (!start || !end) return null;

  const visibleStart = start < windowStart ? windowStart : start;
  const visibleEnd = end > windowEnd ? windowEnd : end;
  if (visibleEnd < windowStart || visibleStart > windowEnd) return null;

  const offsetDays = differenceInCalendarDays(visibleStart, windowStart);
  const spanDays = differenceInCalendarDays(visibleEnd, visibleStart) + 1;
  const barLeft = offsetDays * dayWidth + 2;
  const barWidth = Math.max(spanDays * dayWidth - 4, 6);

  const dragTitle = draft
    ? `${format(parseYmd(draft.start), 'M/d/yyyy')} – ${format(parseYmd(draft.end), 'M/d/yyyy')}`
    : fullTitle;

  const interactionDisabled = disabled || saving;

  return (
    <Box
      title={dragTitle}
      sx={{
        position: 'absolute',
        left: barLeft,
        width: barWidth,
        height: 24,
        display: 'flex',
        alignItems: 'stretch',
        bgcolor: color,
        borderRadius: 1,
        opacity: usingDefaults ? 0.65 : saving ? 0.75 : 1,
        overflow: 'hidden',
        touchAction: 'none',
        userSelect: 'none',
        zIndex: 1,
      }}
    >
      <Box
        onPointerDown={(event) => beginDrag('resize-start', event)}
        sx={{
          width: HANDLE_WIDTH,
          flexShrink: 0,
          cursor: interactionDisabled ? 'default' : 'ew-resize',
        }}
      />
      <Box
        onPointerDown={(event) => beginDrag('move', event)}
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          px: showBarLabel ? 0.75 : 0,
          cursor: interactionDisabled ? 'default' : 'grab',
          '&:active': { cursor: interactionDisabled ? 'default' : 'grabbing' },
        }}
      >
        {showBarLabel && (
          <Typography
            variant="caption"
            noWrap
            sx={{ color: '#fff', fontSize: '0.65rem', fontWeight: 600, pointerEvents: 'none' }}
          >
            {headline}
          </Typography>
        )}
      </Box>
      <Box
        onPointerDown={(event) => beginDrag('resize-end', event)}
        sx={{
          width: HANDLE_WIDTH,
          flexShrink: 0,
          cursor: interactionDisabled ? 'default' : 'ew-resize',
        }}
      />
    </Box>
  );
}
